import { Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useMemo } from 'react';

import { ComposerClientMenuHelper } from './composer-client-menu-helper';
import { ComposerPickerMenuHelper } from './composer-picker-menu-helper';
import { ComposerPickerMenu } from './ComposerPickerMenu';
import type { IComposerClientPickerConfig } from './types';

interface ComposerClientMenuProps {
  clientPicker: IComposerClientPickerConfig;
}

/** The chat's scope control: a pill above the composer opening a searchable client list
 * (Figma A-M, node 849:9725). "All clients" is a real, sendable choice, so it is the first row
 * rather than an empty state. Fully controlled: it renders what it is given and reports picks up. */
export const ComposerClientMenu = ({
  clientPicker,
}: ComposerClientMenuProps) => {
  const { clients, clientId, onClientChange } = clientPicker;
  const options = useMemo(
    () => [
      {
        value: ComposerClientMenuHelper.ALL_CLIENTS_VALUE,
        label: ComposerClientMenuHelper.ALL_CLIENTS_LABEL,
      },
      ...clients.map((client) => ({
        value: String(client.id),
        label: client.name,
      })),
    ],
    [clients]
  );

  const selectedValue = ComposerClientMenuHelper.toValue(clientId);

  return (
    <ComposerPickerMenu
      options={options}
      value={selectedValue}
      onValueChange={(value) =>
        onClientChange(ComposerClientMenuHelper.toClientId(value))
      }
      searchPlaceholder="Search clients"
      emptyText="No clients found."
      widthClassName="w-[300px]"
      trigger={
        <button
          type="button"
          aria-label="Choose a client"
          className={cn(
            ComposerPickerMenuHelper.PILL_CLASS_NAMES,
            'cursor-pointer pl-md pr-lg hover:bg-bg-secondary'
          )}
        >
          <Icons.Building06
            aria-hidden="true"
            className="size-4 shrink-0 text-fg-secondary"
          />
          {ComposerPickerMenuHelper.getSelectedLabel(
            options,
            selectedValue,
            ComposerClientMenuHelper.ALL_CLIENTS_LABEL
          )}
        </button>
      }
    />
  );
};
